import { FormControlProps } from "@chakra-ui/form-control";
import {
  FormControl,
  FormLabel,
  InputGroup,
  StyleProps,
  Textarea as CTextarea,
  useMergeRefs,
} from "@chakra-ui/react";
import { ChakraProps } from "@chakra-ui/system";
import { dataAttr } from "@chakra-ui/utils";
import React, { ChangeEventHandler } from "react";
import { useController } from "react-hook-form";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import {
  InputContent,
  InputMessages,
  InputWrapper,
} from "shared/components/form/InputContent";
import { getFieldError } from "shared/utils/FormUtils";

export interface TextAreaProps extends StyleProps {
  name?: string;
  title: string;
  value: string | null;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onChangeValue?: (value: string) => void;
  isDisabled?: boolean;
  error?: string;
  controlProps?: FormControlProps;
  wrapperProps?: ChakraProps;
}

export type FormTextAreaProps = Omit<TextAreaProps, "value" | "error"> & {
  name: string;
  shouldUnregister?: boolean;
};

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      isDisabled,
      error,
      name,
      title,
      value,
      onChange: onChangeProp,
      onChangeValue,
      controlProps,
      wrapperProps,
      ...props
    },
    ref,
  ) => (
    <FormControl
      isInvalid={!!error}
      isDisabled={isDisabled}
      data-indeterminate={!value ? true : undefined}
      data-disabled={dataAttr(isDisabled)}
      {...controlProps}
    >
      <InputWrapper {...wrapperProps}>
        <InputGroup size="lg">
          <InputContent inputType="textarea">
            <FormLabel fontSize="sm" variant="floating">
              {title}
            </FormLabel>
            <CTextarea
              name={name}
              title={title}
              value={value ?? ""}
              onChange={(e) => {
                onChangeProp?.(e);
                onChangeValue?.(e.target.value);
              }}
              isDisabled={isDisabled}
              isInvalid={!!error}
              variant="unstyled"
              paddingX="16px"
              paddingTop="20px"
              paddingBottom="8px"
              ref={ref}
              fontSize="14px"
              _disabled={{
                cursor: "not-allowed",
                opacity: "unset",
              }}
              {...props}
            />
          </InputContent>
        </InputGroup>
      </InputWrapper>
      <InputMessages error={error} />
    </FormControl>
  ),
);
TextArea.displayName = "TextArea";

export const FormTextArea = React.forwardRef<
  HTMLTextAreaElement,
  FormTextAreaProps
>(({ name, isDisabled, shouldUnregister = true, ...otherProps }, fwRef) => {
  const prefixedName = usePrefixedFieldName(name);
  const {
    field: { ref: fieldRef, ...field },
    formState: { errors },
  } = useController({
    name: prefixedName,
    shouldUnregister,
    defaultValue: "",
  });
  const mergedRef = useMergeRefs(fwRef, fieldRef);
  const isFormDisabled = useIsFormDisabled();
  return (
    <TextArea
      {...field}
      {...otherProps}
      isDisabled={isDisabled || isFormDisabled}
      error={getFieldError(errors, prefixedName)?.message}
      ref={mergedRef}
    />
  );
});
FormTextArea.displayName = "FormTextArea";

export default TextArea;
